import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import {
  NARRATION_STATE_EVENT,
  type NarrationStateDetail,
} from "@/desktop/narration-events";
import { isTauri } from "@/lib/is-tauri";

export type NarrationBoundaryOutcome =
  | "finished"
  | "cancelled"
  | "interrupted"
  | "error"
  | "fallback"
  | "timeout"
  | "aborted";

export async function subscribeNarrationState(
  handler: (detail: NarrationStateDetail) => void,
): Promise<() => void> {
  const windowHandler = (event: Event) => {
    handler((event as CustomEvent<NarrationStateDetail>).detail);
  };
  window.addEventListener(NARRATION_STATE_EVENT, windowHandler);
  let unlisten: UnlistenFn | null = null;
  try {
    if (isTauri()) {
      unlisten = await listen<NarrationStateDetail>(
        NARRATION_STATE_EVENT,
        (event) => handler(event.payload),
      );
    }
  } catch (error) {
    window.removeEventListener(NARRATION_STATE_EVENT, windowHandler);
    throw error;
  }
  return () => {
    window.removeEventListener(NARRATION_STATE_EVENT, windowHandler);
    unlisten?.();
  };
}

interface NarrationBoundaryOptions {
  text: string;
  signal: AbortSignal;
  minVisibleMs: number;
  maxMs: number;
  startTimeoutMs: number;
  startedAt?: number;
  source?: string;
  executionId?: string;
  /** Starts playback only after both local and cross-window listeners exist. */
  onReady?: () => void;
}

export async function waitForNarrationBoundary(
  options: NarrationBoundaryOptions,
): Promise<NarrationBoundaryOutcome> {
  if (options.signal.aborted) return "aborted";
  const startedAt = options.startedAt ?? Date.now();
  let activeUtteranceId: string | null = null;
  let terminal: "finished" | "cancelled" | "interrupted" | "error" | null =
    null;
  let minVisibleElapsed = false;
  let settled = false;
  let minTimer = 0;
  let startTimer = 0;
  let maxTimer = 0;
  let cleanup = () => {};

  let resolveOutcome!: (outcome: NarrationBoundaryOutcome) => void;
  const outcome = new Promise<NarrationBoundaryOutcome>((resolve) => {
    resolveOutcome = resolve;
  });
  const finish = (result: NarrationBoundaryOutcome) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(minTimer);
    window.clearTimeout(startTimer);
    window.clearTimeout(maxTimer);
    options.signal.removeEventListener("abort", onAbort);
    cleanup();
    resolveOutcome(result);
  };
  const maybeFinish = () => {
    if (activeUtteranceId && terminal && minVisibleElapsed) finish(terminal);
  };
  const onAbort = () => finish("aborted");
  let unsubscribe!: () => void;
  try {
    unsubscribe = await subscribeNarrationState((detail) => {
      if (settled || detail.at < startedAt - 250) return;
      // A peer can cancel the shared utterance before this display receives
      // Pause/Repeat. Its local attempt signal owns that transition; this
      // event must not turn a guide pause into a cancelled sequence receipt.
      if (detail.state === "cancelled" && detail.presentationAttemptCancelled)
        return;
      if (!activeUtteranceId) {
        const matchesRequest = options.executionId
          ? detail.executionId === options.executionId
          : options.source
            ? detail.ttsSource === options.source
            : detail.text === options.text;
        if (!matchesRequest) return;
        activeUtteranceId = detail.utteranceId;
        window.clearTimeout(startTimer);
        maxTimer = window.setTimeout(() => finish("timeout"), options.maxMs);
        if (detail.state === "started") return;
      }
      if (detail.utteranceId !== activeUtteranceId) return;
      if (
        detail.state === "finished" ||
        detail.state === "cancelled" ||
        detail.state === "error"
      ) {
        terminal =
          detail.state === "cancelled" && detail.speechInterruptedByUser
            ? "interrupted"
            : detail.state;
        maybeFinish();
      }
    });
  } catch {
    if (options.signal.aborted) return "aborted";
    try {
      options.onReady?.();
    } catch {
      // The caller will use its non-event fallback below.
    }
    return "error";
  }
  if (settled) {
    unsubscribe();
    return outcome;
  }
  cleanup = unsubscribe;
  if (options.signal.aborted) {
    finish("aborted");
    return outcome;
  }
  options.signal.addEventListener("abort", onAbort, { once: true });
  minTimer = window.setTimeout(() => {
    minVisibleElapsed = true;
    maybeFinish();
  }, options.minVisibleMs);
  startTimer = window.setTimeout(() => {
    if (!activeUtteranceId) finish("fallback");
  }, options.startTimeoutMs);
  try {
    options.onReady?.();
  } catch {
    finish("error");
  }
  return outcome;
}
